import {createAction} from 'redux-actions'
import {createActionObject} from '../../redux/utils'
import {addCustomer, addCustomers, addProject, addProjects, addTasks} from '../../entities/actions'
import {getProject, getTask, getTasks, getWorkflowStatuses} from '../../entities/selectors'
import {reloadTasks, removeTasks, setLastCreatedTaskIds, setManagementAssignments, setViewerSettings} from '../common/actions'
import * as ActionTypes from "./actionTypes"
import rpcCall from "../../util/rpc/rpcCalls"
import messageResource from "../../messageResource"
import * as SyncCallbacks from '../syncCallbacks'
import {isTWSEnabled} from "../../features/selectors"
import {WorkflowStatusType} from "../../workflowStatus/constants"
import {getBulkAssignDialogData, getBulkChangeStatusDialogData, getBulkMoveDialogData, getBulkTaskDeleteDialogData,} from './selectors'
import {toast} from '../../toasts/actions'
import {map} from '../../util/rpc/rpcUtils'
import BulkDeleteTasksController from './controller/BulkDeleteTasksController'
import BulkMoveCopyTasksController from './controller/BulkMoveCopyTasksController'
import BulkChangeStatusTasksController from './controller/BulkChangeStatusTasksController'
import BulkChangeAssignmentsTasksController from './controller/BulkChangeAssignmentsTasksController'
import {clearTaskSelectionAction} from '../taskSelection/actions'
import {getSelectedTaskIdsArray} from '../taskSelection/selectors'
import UserWithAccessRights from '../../store/UserWithAccessRights'
import {getLoggedInUserId} from '../../loggedInUser/selectors'
import {arrayToSet} from '../../atUtils'
import {getAssignedTaskToastText, getCopyTasksToastText, getDeletedTasksToastText, getMoveTasksToastText, getStatusChangedTaskToastText} from './utils'
import {NO_SUCH_PROJECT_EXCEPTION, PROJECT_IS_ARCHIVED_EXCEPTION, PROJECT_IS_NOT_ASSIGNED_EXCEPTION} from '../common/ExceptionsTypes'
import {BulkMoveTypes} from 'constants'
import {separateTaskCopySettings} from '../../panels/project/actions'

export const setBulkOperationProgress = createAction(ActionTypes.BULK_OPERATION_PROGRESS_SET)

export const cleanupDialogsDataAction = createAction(ActionTypes.CLEANUP_DIALOGS_DATA)
export const startBulkCopyOperation = createAction(ActionTypes.START_BULK_COPY_OPERATION)

export const startBulkTasksDeleteAction = (onSuccessCb) => (dispatch) => {
    dispatch(createActionObject(ActionTypes.START_BULK_TASKS_DELETE_OPERATION))
    dispatch(loadDeleteDialogDataAction(onSuccessCb))
}

export const loadDeleteDialogDataAction = (onSuccessCb) => (dispatch, getState) => {
    dispatch(createActionObject(ActionTypes.LOAD_BULK_DELETE_DIALOG_DATA))
    rpcCall(
        "TaskManagementService.getDeleteTaskPopupInfo",
        [
            getSelectedTaskIdsArray(getState())
        ],

        (rpcError) => {
            if (rpcError.getTextStatus() === "rpc_customException") {
                switch (rpcError.getErrorThrown().exception) {
                    case "com.actimind.actitime.services.BaseNewTasksService$NoRightsToManageTasksException":
                        alert(messageResource.getMessage("common.no_rights_for_operation_anymore"))
                        window.location.reload()
                        return true
                }
            }
            return false
        }
    ).then((data) => {
        if (data.taskIds.length === 0) {
            alert(messageResource.getMessage('task.delete.js.select_tasks_to_delete'))
        }
        dispatch(createActionObject(ActionTypes.LOAD_BULK_DELETE_DIALOG_DATA_SUCCESS, {...data, entitiesToDeleteCount: data.taskIds.length}))
        onSuccessCb()
    }).catch(() => {
        dispatch(createActionObject(ActionTypes.LOAD_BULK_DELETE_DIALOG_DATA_FAILURE))
    })
}

export const deleteTasksAction = (revokeStatus) => (dispatch, getState) => {
    dispatch(createActionObject(ActionTypes.BULK_TASKS_DELETE))

    const taskIds = getBulkTaskDeleteDialogData(getState()).taskIds

    new BulkDeleteTasksController({
        onProgress: progress => dispatch(setBulkOperationProgress(progress)),
        onSuccess: (result) => {
            dispatch(createActionObject(ActionTypes.BULK_TASKS_DELETE_SUCCESS, result.successTaskIds))
            dispatch(clearTaskSelectionAction())
            dispatch(toast(getDeletedTasksToastText(
                result.successTaskIds,
                result.successTaskIds.length === 1 ? getTask(getState(), result.successTaskIds[0]): null
            )))
            dispatch(removeTasks(result.deletedTaskIds.concat(result.successTaskIds)))
            dispatch(reloadTasks())
        },
        onFailure: (error) => {
            dispatch(createActionObject(ActionTypes.BULK_TASKS_DELETE_FAILURE))

            if (error.getTextStatus() === "rpc_customException") {
                switch (error.getErrorThrown().exception) {
                    case "com.actimind.actitime.services.BaseNewTasksService$NoRightsToManageTasksException":
                        alert(messageResource.getMessage("common.no_rights_for_operation_anymore"))
                        window.location.reload()
                        return true
                }
            }
            return false
        },
    }).processItems(taskIds, [revokeStatus], true)
}

export const startBulkMoveAction = (onSuccessCb) => (dispatch) => {
    dispatch(createActionObject(ActionTypes.START_BULK_MOVE_OPERATION))
    dispatch(loadMoveDialogDataAction(onSuccessCb))
}

export const loadMoveDialogDataAction = (onSuccessCb) => (dispatch, getState) => {
    dispatch(createActionObject(ActionTypes.LOAD_BULK_MOVE_DIALOG_DATA))
    rpcCall(
        "TaskManagementService.getMoveToDialogData",
        [
            getSelectedTaskIdsArray(getState())
        ],
        (rpcError) => {
            if (rpcError.getTextStatus() === "rpc_customException") {
                switch (rpcError.getErrorThrown().exception) {
                    case "com.actimind.actitime.project.NoSuchProjectException":
                        alert(messageResource.getMessage("assign_to.tasks.project_was_deleted"))
                        window.location.reload()
                        return true
                    case "com.actimind.actitime.task.ProjectIsNotAvailableException":
                        alert(messageResource.getMessage("common.no_rights_for_operation_anymore"))
                        window.location.reload()
                        return true
                }
            }
            return false
        }
    ).then((data) => {
        dispatch(createActionObject(ActionTypes.LOAD_BULK_MOVE_DIALOG_DATA_SUCCESS, data))
        onSuccessCb()
    }).catch(() => {
        dispatch(createActionObject(ActionTypes.LOAD_BULK_MOVE_DIALOG_DATA_FAILURE))
    })
}

export const moveTasksAction = (customer, project, moveType, newWorkflowStatusId) => (dispatch, getState) => {
    dispatch(createActionObject(ActionTypes.BULK_TASKS_MOVE))
    const bulkMoveDialogData = getBulkMoveDialogData(getState())
    const taskIds = bulkMoveDialogData.taskIds

    new BulkMoveCopyTasksController({
        method: 'TaskManagementService.moveTasks',
        onProgress: progress => dispatch(setBulkOperationProgress(progress)),
        onSuccess: (result) => {
            dispatch(createActionObject(ActionTypes.BULK_TASKS_MOVE_SUCCESS))
            if (result.successTasksCount > 0) {
                dispatch(reloadTasks())
            }
            dispatch(clearTaskSelectionAction())
            const task = getTask(getState(), taskIds[0])
            dispatch(toast(getMoveTasksToastText(
                result.successTasksCount,
                project,
                result.successTasksCount === 1 ? task : null)
            ))

            if (moveType === BulkMoveTypes.OPEN_TO_ARCHIVED_WITH_RESTORE) {
                if (customer.archived) {
                    dispatch(toast(messageResource.getMessage("status.customer.restored.toast", [customer.name])))
                    dispatch(addCustomer({...customer, archived: false}))
                }
                dispatch(addProject({...project, archived: false}))
                dispatch(toast(messageResource.getMessage("status.project.restored.toast", [project.name])))
            }
            else if (bulkMoveDialogData.includeOpenTasks && (customer.archived || project.archived)) {
                if (result.successTasksCount === 1) {
                    dispatch(toast(messageResource.getMessage("status.task.completed.toast", [task.name])))
                } else {
                    dispatch(toast(messageResource.getMessage("status.tasks.completed.toast")))
                }
            }

            if (result.duplicateNameTasksCount > 0)
                dispatch(toast(messageResource.getMessage('move.tasks.renamed.toast')))

            if (result.invalidTaskStatusCount > 0) {
                dispatch(toast(messageResource.getMessage("task.error.move.task_reopened.bulk")))
            }
        },
        onFailure: (error) => {
            dispatch(createActionObject(ActionTypes.BULK_TASKS_MOVE_FAILURE))
            if (error.errorThrown.exception === "com.actimind.actitime.services.NoRightsForRemoteCallException") {
                alert(messageResource.getMessage("task.list.error.bulk.no_manage_scope_right"))
                window.location.reload()
                return true
            }
            if (error.getTextStatus() === "rpc_customException") {
                switch (error.getErrorThrown().exception) {
                    case NO_SUCH_PROJECT_EXCEPTION:
                        alert(messageResource.getMessage("task.list.error.bulk.project_deleted"))
                        window.location.reload()
                        return true
                    case PROJECT_IS_NOT_ASSIGNED_EXCEPTION:
                        alert(messageResource.getMessage("task.list.error.bulk.project_not_assigned"))
                        window.location.reload()
                        return true
                    case PROJECT_IS_ARCHIVED_EXCEPTION:
                        alert(messageResource.getMessage("task.list.error.bulk.project_archived"))
                        window.location.reload()
                        return true
                }
            }
            return false

        },
    }).processItems(taskIds, [project.id, moveType, newWorkflowStatusId], true)
}

export const copyTasksAction = (taskIds, projectId, settings, operationId) => (dispatch, getState) => {
    const successCb = (result) => {
        dispatch(clearTaskSelectionAction())
        dispatch(toast(getCopyTasksToastText(
            result.successTasksCount,
            getProject(getState(), projectId),
            result.successTasksCount === 1 ? getTask(getState(), taskIds[0]) : null)
        ))
        if (result.duplicateNameTasksCount > 0)
            dispatch(toast(messageResource.getMessage('copy.tasks.renamed.toast')))
    }

    dispatch(copyTasks('TaskManagementService.copyTasks', taskIds, [projectId, separateTaskCopySettings(settings), operationId], successCb))
}

export const copyTasksToProjectsAction = (taskIds, projectOldIdToNewId, settings, operationId) => (dispatch) => {
    dispatch(copyTasks('TaskManagementService.copyTasksToProjects', taskIds, [map(projectOldIdToNewId), separateTaskCopySettings(settings), operationId]))
}

const copyTasks = (method, taskIds, params, successCb = () => {}) => (dispatch) => {
    dispatch(createActionObject(ActionTypes.BULK_TASKS_COPY))

    new BulkMoveCopyTasksController({
        method,
        onProgress: progress => dispatch(setBulkOperationProgress(progress)),
        onSuccess: (result) => {
            dispatch(createActionObject(ActionTypes.BULK_TASKS_COPY_SUCCESS))

            if (result.successTasksCount > 0) {
                dispatch(reloadTasks())
            }
            successCb(result)
        },
        onFailure: (error) => {
            dispatch(createActionObject(ActionTypes.BULK_TASKS_COPY_FAILURE))

            if (error.getTextStatus() === "rpc_customException") {
                switch (error.getErrorThrown().exception) {
                    case "com.actimind.actitime.services.NoRightsForRemoteCallException":
                        alert(messageResource.getMessage("task.list.copy_to.bulk.error.no_manage_scope_right"))
                        window.location.reload()
                        return true
                    case "com.actimind.actitime.task.NoSuchTaskException":
                        alert(messageResource.getMessage("task.list.error.task_deleted"))
                        window.location.reload()
                        return true
                    case "com.actimind.actitime.project.NoSuchProjectException":
                        alert(messageResource.getMessage("task.list.copy_to.bulk.error.project_deleted"))
                        window.location.reload()
                        return true
                    case "com.actimind.actitime.project.ProjectIsNotAssignedException":
                        alert(messageResource.getMessage("task.list.copy_to.bulk.error.project_not_assigned"))
                        window.location.reload()
                        return true
                    case "com.actimind.actitime.task.ProjectIsArchivedException":
                        alert(messageResource.getMessage("task.list.copy_to.bulk.error.project_archived"))
                        window.location.reload()
                        return true
                    case "com.actimind.actitime.customer.NoSuchCustomerException":
                        alert(messageResource.getMessage("project.error.customer_deleted_concurrently"))
                        window.location.reload()
                        return true
                }
            }
            return false

        },
    }).processItems(taskIds, params)
}

export const startBulkAssignAction = (onSuccessCb) => (dispatch) => {
    dispatch(createActionObject(ActionTypes.START_BULK_ASSIGN_TASKS_OPERATION))
    dispatch(loadAssignDialogDataAction(onSuccessCb))
}

export const loadAssignDialogDataAction = (onSuccessCb) => (dispatch, getState) => {
    dispatch(createActionObject(ActionTypes.LOAD_BULK_ASSIGN_DIALOG_DATA))
    rpcCall(
        'TaskManagementService.getAssignTasksPopupInfo',
        [
            getSelectedTaskIdsArray(getState())
        ],
        (rpcError) => {
            if (rpcError.getTextStatus() === "rpc_customException") {
                switch (rpcError.getErrorThrown().exception) {
                    case "com.actimind.actitime.services.BaseNewTasksService$NoRightsToManageTasksException":
                        alert(messageResource.getMessage("common.no_rights_for_operation_anymore"))
                        window.location.reload()
                        return true
                    case "com.actimind.actitime.project.NoSuchProjectException":
                        alert(messageResource.getMessage("assign_to.tasks.project_was_deleted"))
                        window.location.reload()
                        return true
                    case "com.actimind.actitime.task.ProjectIsNotAvailableException":
                        alert(messageResource.getMessage("common.no_rights_for_operation_anymore"))
                        window.location.reload()
                        return true
                }
            }

            return false
        }
    ).then((data) => {
        const loggedInUserId = getLoggedInUserId(getState())
        const allManagers = data.assignedUsersData.users.map(user => {
            const userWithAccessRights = UserWithAccessRights.fromRPC(user)
            Object.assign(userWithAccessRights, user)
            userWithAccessRights.canBeModified = !user.hasManageAssignment
            return userWithAccessRights
        }).sort((u1, u2) => u1.lastName.localeCompare(u2.lastName))
        const availableManagers = allManagers.filter(user => user.assignedToViewer && user.id !== loggedInUserId)
        const selectedUserIds = availableManagers.filter((user) => !user.canBeModified).map(user => user.id)

        dispatch(createActionObject(ActionTypes.LOAD_BULK_ASSIGN_DIALOG_DATA_SUCCESS, {
            ...data.assignedUsersData,
            users: availableManagers,
            selectedUserIds,
            taskIds: data.taskIds,
            numberOfTasks: data.taskIds.length,
        }))
        onSuccessCb()
    }).catch((e) => {
        console.log(e)
        dispatch(createActionObject(ActionTypes.LOAD_BULK_ASSIGN_DIALOG_DATA_FAILURE))
    })
}

const setBulkAssignDialogSelection = createAction(ActionTypes.BULK_ASSIGN_DIALOG_SELECTION_SET)

export const changeBulkAssignDialogSelection = (addedUserIds, removedUserIds) => (dispatch, getState) => {
    const removedUserIdsSet = arrayToSet(removedUserIds)
    let selectedUserIds = getBulkAssignDialogData(getState()).selectedUserIds
                                                             .concat(addedUserIds)
                                                             .filter(userId => !removedUserIdsSet[userId])
    dispatch(setBulkAssignDialogSelection(selectedUserIds))
}

export const changeTasksAssignmentsAction = (userIds) => (dispatch, getState) => {
    dispatch(createActionObject(ActionTypes.BULK_ASSIGN_TASKS))

    const taskIds = getBulkAssignDialogData(getState()).taskIds

    new BulkChangeAssignmentsTasksController({
        onProgress: progress => dispatch(setBulkOperationProgress(progress)),
        onSuccess: (result) => {
            dispatch(createActionObject(ActionTypes.BULK_ASSIGN_TASKS_SUCCESS))
            dispatch(clearTaskSelectionAction())
            dispatch(setViewerSettings(result.viewerSettings))
            dispatch(setManagementAssignments(result.managementAssignments))

            const taskIdsSize = result.taskIds.length
            const userIdsSize = Object.keys(result.userIds).length

           dispatch(toast(getAssignedTaskToastText(
               taskIdsSize,
               userIdsSize,
               taskIdsSize === 1 ? getTask(getState(), result.taskIds[0]) : null
           )))

            if (userIdsSize !== userIds.length) {
                dispatch(toast(messageResource.getMessage('assign_to.tasks.toast.some_users_unassigned')))
            }

            if (taskIds.length !== taskIdsSize) {
                dispatch(reloadTasks())
            }

            // update user settings
            SyncCallbacks.afterBulkChangeTasksAssignmentsCb(result)
        },
        onFailure: (error) => {
            dispatch(createActionObject(ActionTypes.BULK_ASSIGN_TASKS_FAILURE))

            if (error.getTextStatus() === "rpc_customException") {
                switch (error.getErrorThrown().exception) {
                    case "com.actimind.actitime.services.BaseNewTasksService$NoRightsToManageTasksException":
                        alert(messageResource.getMessage("common.no_rights_for_operation_anymore"))
                        window.location.reload()
                        return true
                    case "com.actimind.actitime.project.NoSuchProjectException":
                        alert(messageResource.getMessage("assign_to.tasks.project_was_deleted"))
                        window.location.reload()
                        return true
                    case "com.actimind.actitime.task.ProjectIsNotAvailableException":
                        alert(messageResource.getMessage("common.no_rights_for_operation_anymore"))
                        window.location.reload()
                        return true
                }
            }

            return false
        },
    }).processItems(taskIds, [userIds])
}

export const startBulkChangeTasksStatusAction = (successCb) => (dispatch) => {
    dispatch(createActionObject(ActionTypes.START_BULK_CHANGE_TASKS_STATUS_OPERATION))
    dispatch(loadBulkChangeTasksStatusDialogDataAction(successCb))
}

export const loadBulkChangeTasksStatusDialogDataAction = (successCb) => (dispatch, getState) => {
    dispatch(createActionObject(ActionTypes.LOAD_BULK_CHANGE_STATUS_DIALOG_DATA))
    rpcCall(
        "TaskManagementService.getChangeWorkflowStatusPopupInfo",
        [
            getSelectedTaskIdsArray(getState())
        ],
        (rpcError) => {
            if (rpcError.getTextStatus() === "rpc_customException") {
                switch (rpcError.getErrorThrown().exception) {
                    case "com.actimind.actitime.services.BaseNewTasksService$NoRightsToManageTasksException":
                        alert(messageResource.getMessage("common.no_rights_for_operation_anymore"))
                        window.location.reload()
                        return true
                    case "com.actimind.actitime.project.NoSuchProjectException":
                        alert(messageResource.getMessage("assign_to.tasks.project_was_deleted"))
                        window.location.reload()
                        return true
                    case "com.actimind.actitime.task.ProjectIsNotAvailableException":
                        alert(messageResource.getMessage("common.no_rights_for_operation_anymore"))
                        window.location.reload()
                        return true
                }
            }

            return false
        }
    ).then((data) => {
        dispatch(createActionObject(ActionTypes.LOAD_BULK_CHANGE_STATUS_DIALOG_DATA_SUCCESS, data))
        successCb()
    }).catch(() => {
        dispatch(createActionObject(ActionTypes.LOAD_BULK_CHANGE_STATUS_DIALOG_DATA_FAILURE))
    })
}

export const changeTasksStatusAction = (workflowStatusId, force = false) => (dispatch, getState) => {
    dispatch(createActionObject(ActionTypes.BULK_CHANGE_TASKS_STATUS))

    const state = getState()

    const taskIds = getBulkChangeStatusDialogData(state).taskIds

    const workflowStatusesEnabled = isTWSEnabled(state)
    const workflowStatus = getWorkflowStatuses(state)[workflowStatusId]
    const method = workflowStatusesEnabled
                   ? 'TaskManagementService.changeWorkflowStatusTasks'
                   : 'TaskManagementService.changeStatusTasks'
    const params = workflowStatusesEnabled
                   ? [
            workflowStatus.type,
            workflowStatus.id,
            workflowStatus.name,
            force,
        ]
                   : [
            workflowStatus.type,
            force,
        ]

    new BulkChangeStatusTasksController({
        method,
        onProgress: progress => dispatch(setBulkOperationProgress(progress)),
        onSuccess: (result) => {
            dispatch(createActionObject(ActionTypes.BULK_CHANGE_TASKS_STATUS_SUCCESS))

            const taskIds = result.taskIds
            const completed = workflowStatus.type === WorkflowStatusType.COMPLETED

            const allTasks = getTasks(getState())
            const updatedTasks = []
            /*
               TODO incorrect action name using
               https://github.com/Actimind-Development/actitime/issues/825
             */
            dispatch(setLastCreatedTaskIds(taskIds))

            taskIds.forEach((id) => {
                const task = allTasks[id]
                if (task) {
                    updatedTasks.push({
                        ...allTasks[id],
                        completed: completed,
                        workflowStatusId: workflowStatusId,
                    })
                }
            })
            dispatch(addTasks(updatedTasks))
            dispatch(addProjects(Object.values(result.restoredProjects)))
            dispatch(addCustomers(Object.values(result.restoredCustomers)))

            dispatch(clearTaskSelectionAction())

            dispatch(toast(getStatusChangedTaskToastText(
                taskIds.length,
                taskIds.length === 1 ? getTask(getState(), result.taskIds[0]) : null
            )))
            dispatch(reloadTasks())
        },
        onFailure: (error) => {
            dispatch(createActionObject(ActionTypes.BULK_CHANGE_TASKS_STATUS_FAILURE))

            if (error.getTextStatus() === "rpc_customException") {
                switch (error.getErrorThrown().exception) {
                    case "com.actimind.actitime.services.BaseNewTasksService$NoRightsToManageTasksException":
                        alert(messageResource.getMessage("common.no_rights_for_operation_anymore"))
                        window.location.reload()
                        return true
                    case "com.actimind.actitime.project.NoSuchProjectException":
                        alert(messageResource.getMessage("assign_to.tasks.project_was_deleted"))
                        window.location.reload()
                        return true
                    case "com.actimind.actitime.task.ProjectIsNotAvailableException":
                        alert(messageResource.getMessage("common.no_rights_for_operation_anymore"))
                        window.location.reload()
                        return true
                    case "com.actimind.actitime.task.ProjectIsArchivedException":
                        // confirm reopening of archived project containing this task
                        if (confirm(messageResource.getMessage('task.complete.js.reopening_confirmation_tasks_archived'))) {
                            dispatch(changeTasksStatusAction(workflowStatusId, true))
                        }
                        return true
                    case "com.actimind.actitime.administration.NoSuchWorkflowStatusException":
                        alert(messageResource.getMessage("change_tasks_workflow_status.no_such_workflow_status_error"))
                        window.location.reload()
                        return true
                    case "com.actimind.actitime.services.WorkflowStatusChangedException":
                        alert(messageResource.getMessage("change_tasks_workflow_status.workflow_status_changed_error"))
                        window.location.reload()
                        return true
                    case "com.actimind.actitime.services.WorkflowStatusesFeatureBecameEnabledException":
                        alert(messageResource.getMessage("change_tasks_workflow_status.feature_became_enabled"))
                        window.location.reload()
                        return true
                    case "com.actimind.actitime.services.WorkflowStatusesFeatureBecameDisabledException":
                        alert(messageResource.getMessage("change_tasks_workflow_status.feature_became_disabled"))
                        window.location.reload()
                        return true
                }
            }
            return false
        },
    }).processItems(taskIds, params, true)
}


// WEBPACK FOOTER //
// ./web/js/taskManagement/bulkOperations/actions.js